import * as ToastPrimitives from "@radix-ui/react-toast";
import { AccountInterface, EscalationInterface, EscalationIsAssignedToMember, NotificationInterface } from "@swivl-mono/types";
import { motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useEscalationSound } from "../../Hooks/useEscalationSound";
import CallModel, { CallBarPosition } from "../../Models/CallModel/Call.Model";
import { Member } from "../../Models/Member/Member.Current";
import NotificationsModel from "../../Models/Notifications/Notifications.Model";
import { GroupTicketAlert, useTicketingStore } from "../../Models/Ticketing/Ticketing.Store";
import { UsersModel } from "../../Models/Users/Users.Model";
import { ToastWrapper } from "./ToastWrapper";

interface BaseToastRootProps {
  account?: AccountInterface;
}
export interface ToastData {
  id: string;
  createdAt: Date;
  isExiting?: boolean;
  notification?: NotificationInterface;
  escalation?: EscalationInterface;
  groupTicketAlert?: GroupTicketAlert;
}

const EXIT_ANIMATION_MS = 300;

/**
 * Tracks notifications removed from activeNotifications and keeps them
 * in a local "exiting" set so their ToastWrapper stays mounted long
 * enough to play the slide-out animation.
 */
function useExitingNotifications(activeNotifications: NotificationInterface[] | undefined): [Map<number, NotificationInterface>, number] {
  const prevRef = useRef<NotificationInterface[]>([]);
  const exitingRef = useRef<Map<number, NotificationInterface>>(new Map());
  const [tick, setTick] = useState(0);
  const pendingTimers = useRef<NotificationInterface[]>([]);

  const currentIds = new Set(activeNotifications?.map((n) => n.id) || []);
  const prev = prevRef.current;

  // Synchronously detect removals during render so there is no frame gap
  for (const n of prev) {
    if (!currentIds.has(n.id) && !exitingRef.current.has(n.id)) {
      exitingRef.current.set(n.id, n);
      pendingTimers.current.push(n);
    }
  }

  prevRef.current = activeNotifications || [];

  // Schedule cleanup in an effect to avoid side-effects during render
  useEffect(() => {
    if (pendingTimers.current.length === 0) return;
    const toClean = [...pendingTimers.current];
    pendingTimers.current = [];

    const timer = setTimeout(() => {
      toClean.forEach((n) => exitingRef.current.delete(n.id));
      setTick((v) => v + 1);
    }, EXIT_ANIMATION_MS);

    return () => clearTimeout(timer);
  });

  return [exitingRef.current, tick];
}

const BaseToastRoot = ({ account }: BaseToastRootProps) => {
  const { callBarPosition } = CallModel.useState((state) => state);

  const { activeNotifications } = NotificationsModel.useState((state) => state);
  // Filter out voice notifications. We use the call bar for all voice escalations

  const { unacknowledgedEscalations } = UsersModel.useState((state) => state);
  const groupTicketAlerts = useTicketingStore((s) => s.groupTicketAlerts);
  // No account for an anonymous ticket-access visitor — toasts still need to show.
  const restrictedMode = useTicketingStore((s) => s.restrictedMode);
  const hasSession = Boolean(account) || restrictedMode;
  const hasAssignedEscalations = unacknowledgedEscalations?.some((item) => EscalationIsAssignedToMember(item, Member.id));
  // play escalation sound if there are any unacknowledged escalations assigned to the current user
  useEscalationSound(hasSession && hasAssignedEscalations);

  const [exitingNotifications, exitingTick] = useExitingNotifications(activeNotifications);

  const layoutInfo = useMemo(() => {
    const toastHeight = 74;
    const margin = 9.4;
    // nav bar, padding, and right-positioned voice call bar heights
    const excessHeight = 90 + (callBarPosition !== CallBarPosition.Hidden && callBarPosition === CallBarPosition.BottomRight ? 90 : 0);
    const maxHeight = document.documentElement.clientHeight - excessHeight;

    let data: ToastData[] = [];
    if (activeNotifications) {
      data = activeNotifications.map((notification) => {
        return {
          id: "note" + notification.id,
          createdAt: notification.createdAt,
          notification
        };
      });
    }
    // Include exiting notifications so they remain mounted during exit animation
    exitingNotifications.forEach((notification) => {
      if (!data.some((d) => d.id === "note" + notification.id)) {
        data.push({
          id: "note" + notification.id,
          createdAt: notification.createdAt,
          isExiting: true,
          notification
        });
      }
    });
    if (unacknowledgedEscalations) {
      for (const escalation of unacknowledgedEscalations) {
        data.push({
          id: "escalation" + escalation.id,
          createdAt: escalation.createdAt,
          escalation
        });
      }
    }
    for (const alert of groupTicketAlerts) {
      data.push({ id: alert.id, createdAt: alert.createdAt, groupTicketAlert: alert });
    }
    data.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    const toastsHeight = (data && data.length * (toastHeight + margin)) || 0;
    const overflowingIndex = toastsHeight > maxHeight ? Math.floor((maxHeight - margin) / (toastHeight + margin)) - 1 : data?.length || 0;

    return {
      overflowingIndex,
      maxHeight,
      toastHeight,
      margin,
      toastsToRender: (data?.slice(0, overflowingIndex).reverse() as ToastData[]) || [],
      stackingToasts: (data?.slice(overflowingIndex, overflowingIndex + 2).reverse() as ToastData[]) || []
    };
  }, [activeNotifications, unacknowledgedEscalations, groupTicketAlerts, callBarPosition, exitingTick]);

  return (
    <ToastPrimitives.Provider>
      <ToastPrimitives.Root
        duration={5000}
        open={hasSession && layoutInfo.stackingToasts.length > 0}
        style={{ marginBottom: layoutInfo.stackingToasts.length > 0 ? `${layoutInfo.stackingToasts.length * 5 + 80}px` : "80px" }}
      >
        <div className="relative">
          {layoutInfo.stackingToasts.map((data, index) => (
            <ToastWrapper
              key={data.id}
              style={{
                position: "absolute",
                top: index === 0 ? 0 : `${index * 6}px`,
                zIndex: 100 - index
              }}
              data={data}
            />
          ))}
        </div>
      </ToastPrimitives.Root>
      {layoutInfo.toastsToRender.map((data: ToastData, index: number) => (
        <ToastPrimitives.Root key={data.id} duration={5000} open={hasSession && layoutInfo.toastsToRender.length > 0}>
          <ToastWrapper data={data} />
        </ToastPrimitives.Root>
      ))}
      <motion.div
        initial={false}
        animate={{
          visibility: layoutInfo.toastsToRender.length > 0 ? "visible" : "hidden",
          y: callBarPosition !== CallBarPosition.Hidden && callBarPosition === CallBarPosition.BottomRight ? -82 : 0
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed inset-x-4 bottom-0 z-800 p-4 sm:inset-x-auto sm:right-0 sm:w-1/3"
      >
        <ToastPrimitives.Viewport className="flex-col-reverse space-y-2 overflow-hidden" style={{ maxHeight: layoutInfo.maxHeight }} />
      </motion.div>
    </ToastPrimitives.Provider>
  );
};

export const ToastRoot = BaseToastRoot;
